//Proofed
import { useState, useEffect } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StarIcon from '@mui/icons-material/Star'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { COLOR_PRIMARY, COLOR_TERTIARY, NAVBAR_BG, DARK_600, DARK_800, DARK_900, BG_LIGHT, SECTION_DARK, SECTION_LIGHT } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { HeroHighlightsPanel } from '../../components/HeroHighlightsPanel'
import PageMeta from '../../components/PageMeta'
import { VideoModal } from '../../components/VideoModal'
import { buildApiUrl, CONTRACT_NUM, REVIEWS_URL } from '../../common/common'
import coreVideoThumb from '../../img/corevideothumb.png'
import { StatsSection } from '../../components/StatsSection'
import { CtaSection } from '../../components/CtaSection'
import { CorePricingSection } from '../../components/CorePricing'
import { CORE_VIDEO_ID, freetrialpoints, stats } from '../../common/HomeContent'
import { coreSteps, features, highlights, comparison, faqs, coreHIWSubtitle, coreHIWTitle } from '../../common/ProductCoreContent'
import { WaveDivider } from '../../components/WaveDivider'
import { ProblemBox } from '../../components/ProblemBox'
import { coreTestimonials } from '../../common/Testimonials'
import { coreProblems, coreProblemStatement } from '../../common/ProblemContent'

export default function Core() {
    const [demoOpen, setDemoOpen] = useState(false)
    const [videoOpen, setVideoOpen] = useState(false)
    const [liveTotal, setLiveTotal] = useState<number | null>(null)

    useEffect(() => {
        fetch(buildApiUrl())
            .then(res => res.json())
            .then(data => setLiveTotal(data.total ?? null))
            .catch(() => { })
    }, [])

    return (
        <Box>
            <PageMeta
                title="CFP Core"
                description="Never miss a UK or Ireland public sector tender. CFP Core monitors 200+ procurement portals and delivers personalised daily alerts, buyer intelligence and competitor analysis in one platform."
                canonical="/products/core"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_600} 100%)`,
                ...SECTION_DARK,
                pt: { xs: 4, md: 6 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>

                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 1.5,
                                backgroundColor: `${COLOR_PRIMARY}12`,
                                border: `1px solid ${COLOR_PRIMARY}70`,
                                borderRadius: 2, px: 2, py: 1, mb: 3
                            }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1,
                                    backgroundColor: `${COLOR_PRIMARY}20`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <NotificationsActiveIcon sx={{ color: COLOR_PRIMARY, fontSize: 22 }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: COLOR_PRIMARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Tender Alerting
                                </Typography>
                            </Box>
                            <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '52px' }, mb: 2 }}>
                                CFP <Box component="span" sx={{ color: COLOR_PRIMARY }}>Core</Box>
                            </Typography>
                            <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, mb: 3, lineHeight: 1.5 }}>
                                Stop missing tenders because they're on the wrong portal
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', mb: 4, lineHeight: 1.7 }}>
                                Set filters once. Get one daily email with every matched opportunity from 200+ UK & Ireland portals plus buyer intelligence, competitor insights and framework tracking all in one place
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/register" color={COLOR_PRIMARY} endIcon={<ArrowForwardIcon />}>
                                    Join free for 14 days
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={COLOR_PRIMARY}>
                                    Book a Demo
                                </FancyLightButton>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <HeroHighlightsPanel
                                title="Why Core?"
                                highlights={highlights}
                                points={freetrialpoints}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* Demo video */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="md">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Chip
                            icon={<PlayCircleIcon sx={{ fontSize: '16px !important', color: `${COLOR_PRIMARY} !important` }} />}
                            label="Quick Tour"
                            size="medium"
                            sx={{
                                backgroundColor: `${COLOR_PRIMARY}15`,
                                color: COLOR_PRIMARY,
                                border: `1px solid ${COLOR_PRIMARY}35`,
                                fontWeight: 600,
                                fontSize: '12px',
                                mb: 2,
                            }}
                        />
                        <Typography variant="h3" sx={{ mb: 1 }}>See CFP Core in action</Typography>
                        <Typography color="text.secondary">
                            A 3-minute walkthrough of tender search, daily alerts, <br />competitor intelligence and buyer analysis
                        </Typography>
                    </Box>

                    {/* Thumbnail */}
                    <Box
                        onClick={() => setVideoOpen(true)}
                        sx={{
                            position: 'relative',
                            borderRadius: 3,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                            '&:hover .play-overlay': { backgroundColor: 'rgba(0,0,0,0.45)' },
                            '&:hover .play-icon': { transform: 'scale(1.12)' },
                        }}
                    >
                        <Box
                            component="img"
                            src={coreVideoThumb}
                            alt="CFP Core product demo"
                            sx={{ width: '100%', display: 'block' }}
                        />
                        <Box className="play-overlay" sx={{
                            position: 'absolute', inset: 0,
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'background-color 0.2s',
                        }}>
                            <PlayCircleIcon className="play-icon" sx={{
                                fontSize: 72,
                                color: 'white',
                                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))',
                                transition: 'transform 0.2s',
                            }} />
                        </Box>
                    </Box>
                </Container>
            </Box >
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* Problem bar */}
            <ProblemBox quote={coreProblemStatement} items={coreProblems} />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />


            <HowItWorksSection title={coreHIWTitle} subtitle={coreHIWSubtitle} steps={coreSteps} />

            {/* Stats strip */}
            <StatsSection stats={stats} liveFirstStat={liveTotal} />

            {/* Features grid */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>Everything in Core</Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 8, maxWidth: 520, mx: 'auto' }}>
                        A complete tender intelligence toolkit built for UK & Ireland public sector businesses.
                    </Typography>
                    <Grid container spacing={4}>
                        {features.map(feature => (
                            <Grid item xs={12} sm={6} md={3} key={feature.title}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: 2,
                                            backgroundColor: `${COLOR_PRIMARY}15`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 2,
                                        }}>
                                            <feature.Icon sx={{ color: COLOR_PRIMARY, fontSize: 36 }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ mb: 1, fontSize: '15px' }}>{feature.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: '13px' }}>{feature.body}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* Testimonials */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        textAlign: 'center', mb: 6,
                    }} >
                        <Box
                            component="a"
                            href={REVIEWS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                                gap: 0.75, textDecoration: 'none', cursor: 'pointer',
                                borderRadius: 8,
                                px: 5,
                                py: 1,
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                                '&:hover .review-label': { textDecoration: 'underline' },
                            }}
                        >
                            <Box className="star-row" sx={{ display: 'flex', gap: 0.5, transition: 'opacity 0.2s' }}>
                                {[...Array(5)].map((_, i) => (
                                    <StarIcon key={i} sx={{ color: COLOR_PRIMARY, fontSize: 20 }} />
                                ))}
                            </Box>
                            <Typography className="review-label" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', transition: 'color 0.2s' }}>
                                5.0 on Reviews.io · 52 reviews · 100% recommend
                                <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', ml: 0.5 }} />
                            </Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={4}>
                        {coreTestimonials.map(t => (
                            <Grid item xs={12} md={4} key={t.name}>
                                <Box sx={{
                                    backgroundColor: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 3,
                                    p: 3.5,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}>
                                    <FormatQuoteIcon sx={{ color: COLOR_PRIMARY, fontSize: 32, mb: 2, opacity: 0.7 }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '15px', lineHeight: 1.7, flex: 1, mb: 3, fontStyle: 'italic' }}>
                                        "{t.quote}"
                                    </Typography>
                                    <Box>
                                        <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>{t.name}</Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>✓ Verified Buyer · {t.timeAgo}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* Comparison */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="md">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>CFP Core vs Find a Tender</Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 6, maxWidth: 500, mx: 'auto' }}>
                        Find a Tender is free but it only shows you 60% of the market<br /> Here's what you're missing
                    </Typography>

                    {/* Header row */}
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: '1fr 140px 140px',
                        gap: 0, mb: 0,
                        borderRadius: '12px 12px 0 0',
                        overflow: 'hidden',
                        border: `1px solid ${COLOR_PRIMARY}30`,
                    }}>
                        <Box sx={{ backgroundColor: `${COLOR_PRIMARY}10`, px: 3, py: 2 }} />
                        <Box sx={{ backgroundColor: COLOR_PRIMARY, px: 2, py: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: 'white' }}>CFP Core</Typography>
                        </Box>
                        <Box sx={{ backgroundColor: BG_LIGHT, px: 2, py: 2, textAlign: 'center', borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: 'text.secondary' }}>Find a Tender</Typography>
                        </Box>
                    </Box>

                    {/* Rows */}
                    {comparison.map((row, i) => (
                        <Box key={row.feature} sx={{
                            display: 'grid', gridTemplateColumns: '1fr 140px 140px',
                            borderLeft: `1px solid ${COLOR_PRIMARY}30`,
                            borderRight: `1px solid ${COLOR_PRIMARY}30`,
                            borderBottom: i === comparison.length - 1 ? `1px solid ${COLOR_PRIMARY}30` : '1px solid rgba(0,0,0,0.06)',
                            borderRadius: i === comparison.length - 1 ? '0 0 12px 12px' : 0,
                            overflow: 'hidden',
                            backgroundColor: i % 2 === 0 ? 'white' : `${COLOR_PRIMARY}05`,
                        }}>
                            <Box sx={{ px: 3, py: 1.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '14px', color: 'text.primary' }}>{row.feature}</Typography>
                                {row.tooltip && (
                                    <Tooltip title={row.tooltip} arrow placement="right" componentsProps={{ tooltip: { sx: { maxWidth: 280, fontSize: '12px', lineHeight: 1.6 } } }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help', flexShrink: 0 }} />
                                    </Tooltip>
                                )}
                            </Box>
                            <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${COLOR_PRIMARY}20` }}>
                                {typeof row.cfp === 'boolean'
                                    ? <CheckCircleIcon sx={{ color: COLOR_TERTIARY, fontSize: 20 }} />
                                    : <Typography sx={{ fontSize: '13px', fontWeight: 600, color: COLOR_PRIMARY, textAlign: 'center' }}>{row.cfp}</Typography>
                                }
                            </Box>
                            <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid rgba(0,0,0,0.06)' }}>
                                {typeof row.finder === 'boolean'
                                    ? row.finder
                                        ? <CheckCircleIcon sx={{ color: COLOR_TERTIARY, fontSize: 20 }} />
                                        : <Box sx={{ width: 16, height: 2, backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 1 }} />
                                    : <Typography sx={{ fontSize: '13px', color: 'text.secondary', textAlign: 'center' }}>{row.finder}</Typography>
                                }
                            </Box>
                        </Box>
                    ))}
                </Container>
            </Box>
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* FAQ */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="md">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2, color: 'white' }}>Common questions</Typography>
                    <Typography sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.55)' }}>
                        Everything you need to know before you start
                    </Typography>
                    <Stack spacing={1.5}>
                        {faqs.map(faq => (
                            <Accordion key={faq.q} disableGutters elevation={0} sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px !important',
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { border: `1px solid ${COLOR_PRIMARY}40` },
                            }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{faq.q}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{faq.a}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* <TrustedPartnerSection /> */}

            {/* Pricing tiers */}
            <CorePricingSection />

            <CtaSection onDemoClick={() => setDemoOpen(true)} />
            <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} videoId={CORE_VIDEO_ID} title="CFP Core demo" />
            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box >
    )
}
